import React, { useState } from 'react';
import { TruckIcon, TagIcon, ChevronRightIcon, AwardIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
interface OrderSummaryProps {
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  itemCount: number;
  onOpenCoupons?: () => void;
}
export function OrderSummary({
  subtotal,
  discount,
  tax,
  total,
  itemCount,
  onOpenCoupons
}: OrderSummaryProps) {
  const navigate = useNavigate();
  const [showCoupons, setShowCoupons] = useState(false);
  const [showDiscountBreakdown, setShowDiscountBreakdown] = useState(false);
  const [showShippingBreakdown, setShowShippingBreakdown] = useState(false);

  // Calculate estimated loyalty points (e.g., 1 point per 10 SAR)
  const estimatedPoints = Math.floor((total + 11.99) / 10);
  const shippingFee = 11.99;
  const paymentMethods = ['https://uploadthingy.s3.us-west-1.amazonaws.com/34T2tXtbEwvap5cxibG8Zw/image.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/tUoQjWm1v91aJ1XmKpKUWr/image.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/vxtujNwTU2t3ZDV59PkjmC/image.png'];
  const installmentOptions = [{
    provider: 'Tamara',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/34T2tXtbEwvap5cxibG8Zw/image.png',
    installments: 4,
    amount: (total / 4).toFixed(2)
  }, {
    provider: 'Tabby',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tUoQjWm1v91aJ1XmKpKUWr/image.png',
    installments: 4,
    amount: (total / 4).toFixed(2)
  }];
  return <>
      <div className="sticky top-24" data-id="element-1913" data-mp="358qgwyju65c7">
        <div className="bg-white rounded-lg border border-gray-100 p-4 md:p-6" data-id="element-1914" data-mp="30b83ovysd3ye">
          <h2 className="text-xl font-bold mb-6" data-id="element-1915" data-mp="3093zse0w04y4">Order summary</h2>
          
          {/* Order Summary */}
          <div className="space-y-4 mb-6" data-id="element-1916" data-mp="1n14mv7dynsj">
            <div className="flex justify-between text-sm items-center" data-id="element-1917" data-mp="3syuhsy5n3qzi">
              <span className="text-gray-600 flex items-center gap-1" data-id="element-1918" data-mp="1r2lkmoilnrs3">
                Total ({itemCount} item) 
                <button onClick={() => setShowDiscountBreakdown(true)} className="text-xs text-gray-400 hover:text-gray-600 flex items-center" data-id="element-1919" data-mp="1v46vk4bkpvzn">View details <ChevronDownIcon className="w-3 h-3 ml-0.5" data-id="element-1920" data-mp="1hca7qcgxu892" /></button>
              </span>
              <span className="font-medium" data-id="element-1921" data-mp="1ma2bq4uuxktm">{subtotal.toFixed(2)} SAR</span>
            </div>
            <div className="flex justify-between text-sm items-center" data-id="element-1922" data-mp="17dkwh2jjrm1s">
              <span className="text-gray-600 flex items-center gap-1" data-id="element-1923" data-mp="1cdpi16zbb6yv">
                Shipping Fees 
                <button onClick={() => setShowShippingBreakdown(true)} className="text-xs text-gray-400 hover:text-gray-600 flex items-center" data-id="element-1924" data-mp="yeg46dykwoy2">View details <ChevronDownIcon className="w-3 h-3 ml-0.5" data-id="element-1925" data-mp="13d138a23h2kx" /></button>
              </span>
              <span className="font-medium" data-id="element-1926" data-mp="2ss8pr5u3g4mf">{shippingFee} SAR</span>
            </div>
            
            <div className="pt-4 border-t border-gray-100" data-id="element-1927" data-mp="2xqa7nnfs9xwb">
              <div className="flex justify-between items-end" data-id="element-1928" data-mp="37txl1s1c7bns">
                <span className="font-bold text-[#1E2B55] text-lg" data-id="element-1929" data-mp="37scoxxzdjknt">Grand Total <span className="text-xs font-normal text-gray-500 block" data-id="element-1930" data-mp="32wxhpovvgn0d">Including VAT</span></span>
                <span className="text-xl font-bold text-[#1E2B55]" data-id="element-1931" data-mp="3golpjfz38pbj">
                  {(total + shippingFee).toFixed(2)} SAR
                </span>
              </div>
            </div>
            
            {discount > 0 && <div className="bg-gray-50 rounded-lg p-2.5 flex items-center gap-2 text-gray-500 text-xs" data-id="element-1932" data-mp="3qn1i7ndrk6n5">
                <div className="w-4 h-4 rounded-full border border-gray-300 flex items-center justify-center" data-id="element-1933" data-mp="3lohx1p6zuyvu">i</div>
                <span data-id="element-1934" data-mp="3vkkwuoedilfm">{discount.toFixed(2)} SAR Saved on this order!</span>
              </div>}
          </div>

          {/* Loyalty Points Preview */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4 flex items-center gap-3" data-id="element-1935" data-mp="259yeswel6iu1">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-1936" data-mp="218mxde5v93wx">
              <AwardIcon className="w-4 h-4 text-blue-600" data-id="element-1937" data-mp="27mishghfdw10" />
            </div>
            <div data-id="element-1938" data-mp="27on0oamcxh73">
              <p className="text-sm font-medium text-gray-700" data-id="element-1939" data-mp="2bo47b7diskdu">
                You may earn <span className="font-bold text-[#1E2B55]" data-id="element-1940" data-mp="2bm18bxue3gwz">{estimatedPoints} Nuhdeek points</span> with this order
              </p>
            </div>
          </div>

          {/* Complete Purchase Button */}
          <button onClick={() => navigate('/checkout')} className="w-full bg-[#1E2B55] text-white py-3 rounded-lg font-bold hover:bg-[#162041] transition-colors mb-4 shadow-md hover:shadow-lg" data-id="element-1941" data-mp="1jv4y74zprqde">
            Checkout
          </button>

          {/* Payment Methods */}
          <div className="flex items-center justify-center gap-2 flex-wrap mt-4" data-id="element-1942" data-mp="108ytp8bup1fg">
            <img src="https://cdn.niceonesa.com/web/assets_v2/images/mada.svg?v=2.6.2" alt="Mada" className="h-4" data-id="element-1943" data-mp="106w4gqiivcbs" />
            <img src="https://cdn.niceonesa.com/web/assets_v2/images/visa.svg?v=2.6.2" alt="Visa" className="h-4" data-id="element-1944" data-mp="xoiyl9qggxrm" />
            <img src="https://cdn.niceonesa.com/web/assets_v2/images/master-card.svg?v=2.6.2" alt="Mastercard" className="h-4" data-id="element-1945" data-mp="sppn1muqbgu3" />
            <img src="https://cdn.niceonesa.com/web/assets_v2/images/apple-pay.svg?v=2.6.2" alt="Apple Pay" className="h-4" data-id="element-1946" data-mp="npbjah05ut1v" />
            <span className="text-[10px] font-bold border border-gray-200 px-1.5 py-0.5 rounded text-gray-600" data-id="element-1947" data-mp="johznztr40es">CASH</span>
            <img src="https://cdn.niceonesa.com/web/assets_v2/images/tamara.png?v=2.6.2" alt="Tamara" className="h-4" data-id="element-1948" data-mp="erhoxblyi9hc" />
          </div>
        </div>
      </div>

      {/* Discount Breakdown Bottom Sheet / Modal */}
      <AnimatePresence data-id="element-1949" data-mp="124hz1d75k8">
        {showDiscountBreakdown && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowDiscountBreakdown(false)} className="fixed inset-0 bg-black/50 z-[100]" data-id="element-1950" data-mp="3w4brvrjsr8z7" />
            <motion.div initial={{
          y: '100%',
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: '100%',
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto md:right-auto bg-white rounded-t-3xl md:rounded-2xl z-[101] p-5 pb-safe md:pb-5 w-full md:max-w-lg max-h-[90vh] overflow-y-auto" data-id="element-1951" data-mp="3dgb4ppq8ekn9">
              <div className="flex items-center justify-between mb-5" data-id="element-1952" data-mp="545dtyy85bfn">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1953" data-mp="3mhra1j26vwkm">Discount breakdown</h3>
                <button onClick={() => setShowDiscountBreakdown(false)} className="p-1.5 text-gray-400 hover:text-gray-600" data-id="element-1954" data-mp="3rglvytvge18y">
                  <XIcon className="w-5 h-5" data-id="element-1955" data-mp="j0izlk9sjyzx" />
                </button>
              </div>
              
              <div className="space-y-3" data-id="element-1956" data-mp="nun662f5a50h">
                <div className="flex justify-between text-sm text-gray-600 gap-4" data-id="element-1957" data-mp="28ifs3b7zul55">
                  <span data-id="element-1958" data-mp="23jw9nkslzrhj">Subtotal ({itemCount} item) Excluding VAT</span>
                  <span className="flex-shrink-0" data-id="element-1959" data-mp="23hs5d1qssqgq">{(subtotal + discount).toFixed(2)} SAR</span>
                </div>
                <div className="flex justify-between text-sm text-gray-900 font-medium gap-4" data-id="element-1960" data-mp="2hd41ndklqozr">
                  <span className="uppercase" data-id="element-1961" data-mp="2dc1ywmrh7ut1">ONLINE EXCLUSIVE _ Save 50%</span>
                  <span className="flex-shrink-0" data-id="element-1962" data-mp="1pkdahhhrzqhd">-{discount.toFixed(2)} SAR</span>
                </div>
                
                <div className="border-t border-dashed border-gray-200 pt-3 mt-1" data-id="element-1963" data-mp="1knd0wwlpw5li">
                  <div className="flex justify-between text-sm font-bold text-gray-900 mb-2 gap-4" data-id="element-1964" data-mp="1yiozjf2zug7p">
                    <span data-id="element-1965" data-mp="1tnt6cbm0tmt0">Total ({itemCount} item) Excluding VAT</span>
                    <span className="flex-shrink-0" data-id="element-1966" data-mp="39yng040i44iy">{(subtotal - tax).toFixed(2)} SAR</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 gap-4" data-id="element-1967" data-mp="350lw5gv9416f">
                    <span data-id="element-1968" data-mp="3p4jdsgkv3c0x">+item VAT (15%)</span>
                    <span className="flex-shrink-0" data-id="element-1969" data-mp="3u1tbejiemtzu">{tax.toFixed(2)} SAR</span>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-3 mt-1" data-id="element-1970" data-mp="1xgz348kb6v4">
                  <div className="flex justify-between font-bold text-gray-900 text-base gap-4" data-id="element-1971" data-mp="6pikpgrme9xt">
                    <span data-id="element-1972" data-mp="boleg525gtqh">Total ({itemCount} item) Including VAT</span>
                    <span className="flex-shrink-0" data-id="element-1973" data-mp="bqfzdjdf8luk">{subtotal.toFixed(2)} SAR</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Shipping Breakdown Bottom Sheet / Modal */}
      <AnimatePresence data-id="element-1974" data-mp="pnbcpo0pgzgf">
        {showShippingBreakdown && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowShippingBreakdown(false)} className="fixed inset-0 bg-black/50 z-[100]" data-id="element-1975" data-mp="2neo2eq7r2s4d" />
            <motion.div initial={{
          y: '100%',
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: '100%',
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto md:right-auto bg-white rounded-t-3xl md:rounded-2xl z-[101] p-5 pb-safe md:pb-5 w-full md:max-w-lg max-h-[90vh] overflow-y-auto" data-id="element-1976" data-mp="2f58g4a0g560f">
              <div className="flex items-center justify-between mb-5" data-id="element-1977" data-mp="2tt3cc0dnxsi9">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1978" data-mp="2ov1ufirz3z8d">Shipping breakdown</h3>
                <button onClick={() => setShowShippingBreakdown(false)} className="p-1.5 text-gray-400 hover:text-gray-600" data-id="element-1979" data-mp="2ox4l850y9we5">
                  <XIcon className="w-5 h-5" data-id="element-1980" data-mp="1df3ecuydocxn" />
                </button>
              </div>
              
              <div className="space-y-6" data-id="element-1981" data-mp="1jxud1i3ib14r">
                {/* Shipment 1 */}
                <div data-id="element-1982" data-mp="1ezsucy9zt3tt">
                  <div className="inline-flex items-center gap-1 bg-[#4ADE80] text-white px-2 py-0.5 rounded text-[10px] font-bold mb-2" data-id="element-1983" data-mp="1f0c2jn9ucdun">
                    <TruckIcon className="w-3 h-3" data-id="element-1984" data-mp="1ss8r5hcaw5ma" /> Express
                  </div>
                  <div className="flex justify-between text-sm text-gray-900 gap-4 mb-2" data-id="element-1985" data-mp="1nzfnkwskj3bs">
                    <span data-id="element-1986" data-mp="22ntxtssbdu2d">1x Express delivery item(s)</span>
                    <span className="font-bold flex-shrink-0" data-id="element-1987" data-mp="1xqajksvm9haf">0 SAR</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2" data-id="element-1988" data-mp="2bpjo2vagmmyk">
                    <div className="h-full bg-[#84cc16] w-[30%] rounded-full" data-id="element-1989" data-mp="27o85h9q071zv"></div>
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-1990" data-mp="hbip8aewtzyh">
                    Spend <span className="font-bold text-gray-900" data-id="element-1991" data-mp="hc26rq0nembz">92.50 SAR</span> more to get FREE shipping on Express items.
                  </p>
                </div>

                <div className="border-t border-gray-100" data-id="element-1992" data-mp="37bfh4glxmlx"></div>

                {/* Shipment 2 */}
                <div data-id="element-1993" data-mp="76skxxw1ajr3">
                  <div className="inline-flex items-center gap-1 bg-[#1E2B55] text-white px-2 py-0.5 rounded text-[10px] font-bold mb-2" data-id="element-1994" data-mp="772bpnowkuxu">
                    <TruckIcon className="w-3 h-3" data-id="element-1995" data-mp="3uko6krd1oori" /> Standard
                  </div>
                  <div className="flex justify-between text-sm text-gray-900 gap-4 mb-2" data-id="element-1996" data-mp="3uil77gq05ja4">
                    <span data-id="element-1997" data-mp="3fprntk7m0j06">1x Standard delivery item(s)</span>
                    <span className="font-bold flex-shrink-0" data-id="element-1998" data-mp="3knjhojrtef70">{shippingFee} SAR</span>
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-1999" data-mp="36ojt1c4ay0au">
                    Estimated delivery: 2-3 business days
                  </p>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}